import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ref, onValue, get } from 'firebase/database';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CourseCatalog } from '../components/cursos/CourseCatalog';
import { CourseDetailModal } from '../components/cursos/CourseDetailModal';
import { CoursePlanModal } from '../components/cursos/CoursePlanModal';
import { VirtualClassroom } from '../components/cursos/VirtualClassroom';
import { VortexProfileModal } from '../components/cursos/VortexProfileModal';
import { LectorTemasView } from '../components/cursos/LectorTemasView';
import { COURSES_DATA } from '../data/coursesData';
import { database } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { useAdmin } from '../hooks/useAdmin';
import { normalizeCourse, isCourseForProfile } from '../utils/courseStructure';
import { enrichCourseWithResumen } from '../services/academyFirestore';
import '../styles/CursosVortex.css';

const simSelectStyle = {
  padding: '7px 10px', borderRadius: '8px', background: '#0a1a2a',
  border: '1px solid rgba(197,160,89,0.3)', color: '#e2e8f0', fontSize: '0.75rem', outline: 'none',
};

export default function CursosVortexPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profileData, isCursoPeriodoActivo, isAuthenticated } = useAuth();
  const { isAdmin } = useAdmin(user);

  // Acceso a cursos: única y exclusivamente haber comprado el periodo en curso
  // (cursoPeriodoAcceso === periodoActual en licencesPremium), o ser admin.
  const isSubscribed = isCursoPeriodoActivo || isAdmin;

  const [currentView, setCurrentView]         = useState('catalog'); // 'catalog' | 'classroom' | 'lector'
  const [selectedCourse, setSelectedCourse]   = useState(null);
  const [rtdbCourses, setRtdbCourses]        = useState(null); // null = aún no cargado

  // ── Simulación de perfil alumno (solo admin) ──
  const [simProfile, setSimProfile] = useState(null); // { jerarquia, cuerpoServicio, especialidad }
  const [simMeta, setSimMeta] = useState({ cuerpos: [], especialidades: {}, grados: [] });

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    get(ref(database, 'app_metadata'))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const data = snap.val();
        setSimMeta({
          cuerpos: data.cuerpos_y_servicios ? Object.values(data.cuerpos_y_servicios) : [],
          especialidades: data.especialidades || {},
          grados: data.grados ? Object.values(data.grados) : [],
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin]);

  /** Perfil efectivo: el simulado si el admin eligió uno; si no, el real del usuario. */
  const academyProfile =
    isAdmin && simProfile
      ? { jerarquia: simProfile.jerarquia, cuerpoServicio: simProfile.cuerpoServicio || '', especialidad: simProfile.especialidad || '' }
      : {
          jerarquia: profileData?.jerarquia || profileData?.grado || '',
          cuerpoServicio: profileData?.cuerpoServicio || '',
          especialidad: profileData?.especialidad || '',
        };
  const hasAcademyProfile = !!(academyProfile.jerarquia && academyProfile.cuerpoServicio);
  const simulating = !!(isAdmin && simProfile);

  // Modal de resumen de temario
  const [detailCourse, setDetailCourse]       = useState(null);
  const [isDetailOpen, setIsDetailOpen]       = useState(false);

  // Modal de selección de plan / licencia
  const [planCourse, setPlanCourse]           = useState(null);
  const [isPlanOpen, setIsPlanOpen]           = useState(false);

  // Modal de perfil Vortex Academy (jerarquía / cuerpo / especialidad)
  const [isProfileOpen, setIsProfileOpen]     = useState(false);
  const [pendingCourse, setPendingCourse]     = useState(null);

  // Carga los cursos desde RTDB (donde el admin guarda contenido). Fallback a datos estáticos.
  useEffect(() => {
    const unsubscribe = onValue(ref(database, 'vortex_web_courses'), (snap) => {
      if (snap.exists()) {
        const val = snap.val();
        const list = Object.keys(val).map((id) => normalizeCourse({ id, ...val[id] }));
        setRtdbCourses(list.length > 0 ? list : COURSES_DATA);
      } else {
        setRtdbCourses(COURSES_DATA);
      }
    }, () => setRtdbCourses(COURSES_DATA));
    return () => unsubscribe();
  }, []);

  // Enriquece los cursos con su resumen real (temas/lecciones/exámenes) calculado
  // desde Firestore vortexAcademyAreas. Prioriza el override manual del admin.
  const [enrichedCourses, setEnrichedCourses] = useState(null);
  useEffect(() => {
    if (!rtdbCourses) return undefined;
    let cancelled = false;
    Promise.all((rtdbCourses || []).map(enrichCourseWithResumen))
      .then((list) => { if (!cancelled) setEnrichedCourses(list); })
      .catch(() => { if (!cancelled) setEnrichedCourses(rtdbCourses); });
    return () => { cancelled = true; };
  }, [rtdbCourses]);

  const allCourses = enrichedCourses || rtdbCourses || COURSES_DATA;

  // Cursos visibles según el perfil del usuario (jerarquía/cuerpo/especialidad)
  const visibleCourses = isAdmin
    ? allCourses
    : allCourses.filter((course) => isCourseForProfile(course, academyProfile));

  /* ── Auto-open course from ?courseId=xxx (admin panel deep link) ── */
  useEffect(() => {
    const courseId = searchParams.get('courseId');
    if (!courseId) return;
    const course = allCourses.find(c => c.id === courseId);
    if (course && isSubscribed) {
      setSelectedCourse(course);
      setCurrentView('classroom');
    }
  }, [searchParams, rtdbCourses, isSubscribed]);

  /* ── Handlers ── */
  const handleOpenDetail = (course) => {
    setDetailCourse(course);
    setIsDetailOpen(true);
  };

  const handleOpenPlanModal = (course) => {
    setPlanCourse(course);
    setIsPlanOpen(true);
  };

  const handleSelectCourse = (course) => {
    // Sin licencia activa en licencesPremium no se puede entrar al aula
    if (!isSubscribed) {
      handleOpenPlanModal(course);
      return;
    }
    // Si el usuario aún no configuró su perfil, se le pide antes de entrar al aula
    if (!hasAcademyProfile && user) {
      setPendingCourse(course);
      setIsProfileOpen(true);
      return;
    }
    setSelectedCourse(course);
    setCurrentView('classroom');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToCatalog = () => {
    setCurrentView('catalog');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenLector = () => {
    // Sin licencia activa en licencesPremium no se puede usar el Lector
    if (!isSubscribed) {
      handleOpenPlanModal(allCourses[0] || null);
      return;
    }
    if (!hasAcademyProfile && user) {
      setPendingCourse(null);
      setIsProfileOpen(true);
      return;
    }
    setCurrentView('lector');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRequireAuth = () => {
    navigate('/purchase-license');
  };

  const handleProfileSaved = () => {
    if (pendingCourse) {
      if (!isSubscribed) {
        handleOpenPlanModal(pendingCourse);
        setPendingCourse(null);
        return;
      }
      setSelectedCourse(pendingCourse);
      setCurrentView('classroom');
      setPendingCourse(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="vx-cursos-page">
      {currentView !== 'classroom' && <Header />}

      {currentView !== 'classroom' && isAdmin && (
        <div style={{
          background: 'rgba(15,32,53,0.9)', borderBottom: '1px solid rgba(197,160,89,0.25)',
          padding: '10px 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px',
        }}>
          <span style={{
            fontSize: '0.68rem', color: '#c5a059', fontFamily: 'monospace', fontWeight: 'bold',
            textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
          }}>
            🛠️ Simular perfil alumno
          </span>
          <select
            value={simProfile?.jerarquia || ''}
            onChange={(e) => setSimProfile((prev) => ({ ...(prev || {}), jerarquia: e.target.value }))}
            style={simSelectStyle}
          >
            <option value="">Jerarquía...</option>
            {simMeta.grados.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select
            value={simProfile?.cuerpoServicio || ''}
            onChange={(e) => setSimProfile((prev) => ({ ...(prev || {}), cuerpoServicio: e.target.value, especialidad: '' }))}
            style={simSelectStyle}
          >
            <option value="">Cuerpo / Servicio...</option>
            {simMeta.cuerpos.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={simProfile?.especialidad || ''}
            onChange={(e) => setSimProfile((prev) => ({ ...(prev || {}), especialidad: e.target.value }))}
            style={{ ...simSelectStyle, opacity: simProfile?.cuerpoServicio ? 1 : 0.5 }}
          >
            <option value="">Especialidad...</option>
            {(simMeta.especialidades[simProfile?.cuerpoServicio] || []).map((esp) => (
              <option key={esp} value={esp}>{esp}</option>
            ))}
          </select>
          {simulating && (
            <button
              onClick={() => setSimProfile(null)}
              style={{
                background: 'transparent', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5',
                borderRadius: '8px', padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
              }}
            >
              Quitar simulación
            </button>
          )}
          {simulating && (
            <span style={{ fontSize: '0.7rem', color: '#34d399', fontFamily: 'monospace' }}>
              Viendo como: {simProfile.jerarquia}{simProfile.cuerpoServicio ? ` · ${simProfile.cuerpoServicio}` : ''}{simProfile.especialidad ? ` · ${simProfile.especialidad}` : ''}
            </span>
          )}
        </div>
      )}

      <main className={`vx-cursos-main ${currentView === 'classroom' ? 'vx-cursos-main-full' : ''}`}>
        {currentView === 'catalog' ? (
          <CourseCatalog
            courses={visibleCourses}
            isSubscribed={isSubscribed}
            isAuthenticated={isAuthenticated}
            onSelectCourse={handleSelectCourse}
            onOpenDetailModal={handleOpenDetail}
            onEnroll={handleOpenPlanModal}   /* ← abre el modal de planes */
            onOpenAuth={handleRequireAuth}
            onOpenLector={handleOpenLector}
          />
        ) : currentView === 'lector' ? (
          <LectorTemasView
            academyProfile={academyProfile}
            onBack={handleBackToCatalog}
            isSubscribed={isSubscribed}
          />
        ) : selectedCourse ? (
          <VirtualClassroom
            course={selectedCourse}
            uid={user?.uid || null}
            userName={user?.displayName || (user?.email ? user.email.split('@')[0] : 'Estudiante')}
            userEmail={user?.email || ''}
            academyProfile={academyProfile}
            onBackToCatalog={handleBackToCatalog}
            isSubscribed={isSubscribed}
          />
        ) : null}
      </main>

      {/* ── Modal Resumen de Temario ── */}
      <CourseDetailModal
        course={detailCourse}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        isSubscribed={isSubscribed}
        isAuthenticated={isAuthenticated}
        onEnroll={handleOpenPlanModal}    /* ← también abre el modal de planes */
        onStartCourse={handleSelectCourse}
      />

      {/* ── Modal Selección de Plan/Vigencia ── */}
      <CoursePlanModal
        isOpen={isPlanOpen}
        onClose={() => setIsPlanOpen(false)}
        course={planCourse}
        user={user}
        isAuthenticated={isAuthenticated}
        onRequireAuth={handleRequireAuth}
      />

      {/* ── Modal Perfil Vortex Academy ── */}
      <VortexProfileModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        onSaved={handleProfileSaved}
        uid={user?.uid || null}
      />

      {currentView !== 'classroom' && <Footer />}
    </div>
  );
}
